import { useEffect } from 'react';
import { setPageMeta } from '../utils/pageMeta';
import { Camera, FileText, Share2, Download, Check, ArrowRight, Zap, Users, Briefcase, PiggyBank, CreditCard, TrendingUp, Building2, Shield, Lock, HardDrive, Sparkles } from 'lucide-react';
import { T, LogoMark, Wordmark } from '../theme';

const FEATURES = [
  {
    icon: CreditCard,
    title: 'Import CC & Bank Statements',
    desc: 'Import your credit card and bank statements. Classify each transaction as business or personal — without ever separating your accounts.',
    color: T.tealDeep,
    bg: T.tealPale,
  },
  {
    icon: TrendingUp,
    title: 'Business & Financial Summaries',
    desc: 'Real-time P&L, income vs. expenses by category, and a Tax Summary with IRS Schedule C breakdown — all automatically updated.',
    color: T.amberDeep,
    bg: T.amberPale,
  },
  {
    icon: Camera,
    title: 'AI Receipt Scanning',
    desc: 'Snap a photo of any receipt — AI instantly extracts vendor, amount, tax, tip, date, and category. No typing required.',
    color: T.tealDeep,
    bg: T.tealPale,
  },
  {
    icon: FileText,
    title: 'Multiple Reports',
    desc: 'Organize expenses into separate reports by project, trip, client, or time period. Switch between them instantly.',
    color: T.amberDeep,
    bg: T.amberPale,
  },
  {
    icon: Download,
    title: 'Tax-Ready Export',
    desc: 'IRS Schedule C categories, business classification, and export-ready reports as CSV, Excel, PDF, or a formatted Tax Report for your accountant.',
    color: T.tealDeep,
    bg: T.tealPale,
  },
  {
    icon: Share2,
    title: 'Share & Collaborate',
    desc: 'Share reports with clients, managers, or your accountant with view-only or full-edit access — or send a live review link.',
    color: T.amberDeep,
    bg: T.amberPale,
  },
];

const PERSONAS = [
  {
    page: null,
    primaryCta: 'Get started free',
    icon: Building2,
    color: T.tealDeep,
    bg: T.tealPale,
    badge: 'SOLOPRENEURS & SMALL BUSINESS',
    title: 'One account, mixed use — sorted.',
    desc: 'Your business runs from a personal credit card. That\'s fine. Import your statements, tag what\'s business, leave personal alone. MySpend keeps your books organized without requiring you to restructure your financial life.',
    bullets: ['Import CC & bank statements in seconds', 'Classify business vs. personal per transaction', 'P&L, financial summary & IRS tax categories'],
  },
  {
    page: 'consultant',
    icon: Briefcase,
    color: T.amberDeep,
    bg: T.amberPale,
    badge: 'CONSULTANTS',
    title: 'Independent consultants',
    desc: 'Separate expenses by client, send live review links for approval, and get reimbursed faster — no spreadsheet juggling.',
    bullets: ['Per-client expense reports', 'Live review links for client approval', 'Financial summary by client or project'],
  },
  {
    page: 'gig',
    icon: Users,
    color: T.tealDeep,
    bg: T.tealPale,
    badge: 'GIG ECONOMY',
    title: 'Drivers, dashers & delivery',
    desc: 'Every mile, phone charge, and platform fee is a deduction. MySpend tracks them all and keeps you audit-ready at tax time.',
    bullets: ['Mileage & vehicle expenses', 'Platform fee tracking', 'IRS-ready tax export'],
  },
  {
    page: 'personal',
    icon: PiggyBank,
    color: T.amberDeep,
    bg: T.amberPale,
    badge: 'PERSONAL FINANCE',
    title: 'Households & personal budgets',
    desc: 'Finally know where your money goes. Track daily spending, spot patterns, and share a household report — free forever.',
    bullets: ['Daily spend tracking by category', 'Monthly budget visibility', 'Free forever — no credit card'],
  },
];


// ── App screen mockups ────────────────────────────────────────────────────────

function FSVMockup() {
  const MONO = "'Courier New', monospace";
  return (
    <div style={{
      width: 224, background: T.appBg, borderRadius: 28,
      overflow: 'hidden', boxShadow: '0 24px 60px rgba(11,30,45,0.32)',
      border: '6px solid #0B1E2D',
    }}>
      {/* Nav header */}
      <div style={{ background: T.gradNavy, padding: '10px 10px 8px' }}>
        <div style={{ display: 'flex', gap: 2, marginBottom: 7, overflowX: 'hidden' }}>
          {[
            { label: 'Finances', active: false },
            { label: 'Summary',  active: true  },
            { label: 'Tax',      active: false },
            { label: 'Expenses', active: false },
            { label: '+ Add',    active: false },
          ].map(tab => (
            <div key={tab.label} style={{
              fontSize: 7, fontWeight: 700, padding: '3px 5px', borderRadius: 4,
              background: tab.active ? 'rgba(255,255,255,0.18)' : 'transparent',
              color: tab.active ? '#fff' : 'rgba(255,255,255,0.38)',
              whiteSpace: 'nowrap',
            }}>{tab.label}</div>
          ))}
        </div>
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: 'rgba(255,255,255,0.1)', borderRadius: 6, padding: '3px 8px',
        }}>
          <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#05C4B0' }} />
          <span style={{ fontSize: 7.5, color: 'rgba(255,255,255,0.65)', fontWeight: 600 }}>This Month</span>
        </div>
      </div>

      <div style={{ padding: '8px 8px 10px', background: T.appBg }}>
        {/* P&L cards */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 7 }}>
          {[
            { label: 'Income',   amount: '$22.4k', color: '#15803d', bg: '#F0FDF4', border: '#BBF7D0', icon: '▲' },
            { label: 'Expenses', amount: '$14.8k', color: '#dc2626', bg: '#FEF2F2', border: '#FECACA', icon: '▼' },
            { label: 'Net',      amount: '+$7.6k', color: '#15803d', bg: '#F0FDF4', border: '#BBF7D0', icon: '—' },
          ].map(c => (
            <div key={c.label} style={{ flex: 1, background: c.bg, borderRadius: 7, padding: '5px', border: `1px solid ${c.border}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                <span style={{ fontSize: 7, color: c.color, lineHeight: 1 }}>{c.icon}</span>
                <span style={{ fontSize: 6, fontWeight: 700, color: c.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>{c.label}</span>
              </div>
              <div style={{ fontSize: 9.5, fontWeight: 900, color: c.color, fontFamily: MONO }}>{c.amount}</div>
            </div>
          ))}
        </div>

        {/* 6-Month Trend */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', padding: '6px 7px', marginBottom: 6 }}>
          <div style={{ fontSize: 6.5, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>6-Month Trend</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', height: 38 }}>
            {[
              { label: 'Dec', inc: 55, exp: 70 },
              { label: 'Jan', inc: 65, exp: 55 },
              { label: 'Feb', inc: 72, exp: 62 },
              { label: 'Mar', inc: 85, exp: 68 },
              { label: 'Apr', inc: 78, exp: 63 },
              { label: 'May', inc: 100, exp: 75 },
            ].map((m, i, arr) => (
              <div key={m.label} style={{
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
                borderRight: i < arr.length - 1 ? '1px solid rgba(0,0,0,0.07)' : 'none',
                paddingRight: i < arr.length - 1 ? 1 : 0,
                paddingLeft: i > 0 ? 1 : 0,
              }}>
                <div style={{ width: '100%', display: 'flex', gap: 1, alignItems: 'flex-end', height: 30 }}>
                  <div style={{ flex: 1, background: '#15803d', borderRadius: '1px 1px 0 0', height: `${m.inc}%`, minHeight: 2 }} />
                  <div style={{ flex: 1, background: '#dc2626', borderRadius: '1px 1px 0 0', height: `${m.exp}%`, minHeight: 2 }} />
                </div>
                <div style={{ fontSize: 5.5, color: '#9CA3AF', textAlign: 'center' }}>{m.label}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <span style={{ fontSize: 6, color: '#15803d', fontWeight: 700 }}>█ Income</span>
            <span style={{ fontSize: 6, color: '#dc2626', fontWeight: 700 }}>█ Expenses</span>
          </div>
        </div>

        {/* Expenses by Category */}
        <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #E5E7EB', padding: '6px 7px' }}>
          <div style={{ fontSize: 6.5, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Expenses by Category (4)</div>
          {[
            { label: 'Office & Biz', pct: 100, amount: '$3,200' },
            { label: 'Travel',       pct: 77,  amount: '$2,480' },
            { label: 'Food & Dining',pct: 47,  amount: '$1,520' },
            { label: 'Software',     pct: 27,  amount: '$880'   },
          ].map(row => (
            <div key={row.label} style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
              <div style={{ width: 46, fontSize: 6.5, color: '#374151', textAlign: 'right', flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.label}</div>
              <div style={{ flex: 1, height: 5, background: '#F3F4F6', borderRadius: 3, overflow: 'hidden' }}>
                <div style={{ width: `${row.pct}%`, height: '100%', background: '#dc2626', borderRadius: 3 }} />
              </div>
              <div style={{ width: 30, fontSize: 6.5, fontWeight: 700, color: '#dc2626', textAlign: 'right', flexShrink: 0, fontFamily: MONO }}>{row.amount}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function ScanMockup() {
  return (
    <div style={{
      width: 224, background: '#fff', borderRadius: 28,
      overflow: 'hidden', boxShadow: '0 24px 60px rgba(11,30,45,0.32)',
      border: '6px solid #0B1E2D',
    }}>
      {/* Header — matches ExpenseEntryView "Scan Receipt" header row */}
      <div style={{ padding: '11px 13px 9px', display: 'flex', alignItems: 'center', gap: 8, borderBottom: '1px solid #F3F4F6', background: '#fff' }}>
        <span style={{ fontSize: 16, color: '#6B7280', lineHeight: 1, fontWeight: 300 }}>×</span>
        <span style={{ fontSize: 11.5, fontWeight: 800, color: '#111' }}>Scan Receipt</span>
      </div>

      {/* Camera viewfinder with receipt visible */}
      <div style={{ background: '#1C1C1E', margin: '9px 9px 0', borderRadius: 10, padding: '9px' }}>
        <div style={{ border: '1.5px solid rgba(5,196,176,0.55)', borderRadius: 6, padding: '7px 9px' }}>
          <div style={{ textAlign: 'center', paddingBottom: 5, borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: 5 }}>
            <div style={{ fontSize: 8, fontWeight: 800, color: 'rgba(255,255,255,0.72)', letterSpacing: 0.8 }}>BLUE BOTTLE COFFEE</div>
            <div style={{ fontSize: 6, color: 'rgba(255,255,255,0.28)', marginTop: 1 }}>Mission St · San Francisco CA</div>
          </div>
          {[['Latte 12oz', '$6.00'], ['Oat Milk', '$0.75'], ['Almond Croissant', '$4.50']].map(([k, v]) => (
            <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '1.5px 0' }}>
              <span style={{ fontSize: 6.5, color: 'rgba(255,255,255,0.3)' }}>{k}</span>
              <span style={{ fontSize: 6.5, color: 'rgba(255,255,255,0.3)' }}>{v}</span>
            </div>
          ))}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, paddingTop: 4, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <span style={{ fontSize: 7.5, fontWeight: 800, color: 'rgba(255,255,255,0.8)' }}>TOTAL</span>
            <span style={{ fontSize: 7.5, fontWeight: 800, color: '#fff' }}>$12.21</span>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: 6 }}>
          <span style={{ fontSize: 7, color: '#05C4B0', fontWeight: 700 }}>✓ AI analysis complete</span>
        </div>
      </div>

      {/* Extracted fields — mirrors the ExpenseForm pre-filled state */}
      <div style={{ padding: '8px 11px 11px', background: '#fff' }}>
        <div style={{ fontSize: 7, fontWeight: 800, color: '#0A7C8A', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>AI Extracted</div>
        {[
          ['Vendor',   'Blue Bottle Coffee'],
          ['Total',    '$12.21'],
          ['Tax',      '$0.96'],
          ['Subtotal', '$11.25'],
          ['Date',     'May 9, 2026'],
          ['Category', 'Food & Dining'],
        ].map(([label, value]) => (
          <div key={label} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '4px 0', borderBottom: '1px solid #F3F4F6',
          }}>
            <span style={{ fontSize: 8, color: '#9CA3AF', fontWeight: 600 }}>{label}</span>
            <span style={{ fontSize: 8, fontWeight: 700, color: '#111', maxWidth: 108, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</span>
          </div>
        ))}
        <div style={{
          marginTop: 9, padding: '8px', borderRadius: 8,
          background: 'linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)',
          color: '#fff', fontSize: 9, fontWeight: 800, textAlign: 'center',
        }}>
          Save Expense
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function LandingPage({ forceShow, onSignIn, onSignUp, onSkip, onPricing, user }) {
  useEffect(() => {
    setPageMeta({
      title: 'AI powered finances for everyone',
      description: 'Talk to your finances. Get AI insights. Import credit card and bank statements, scan receipts, and get a real-time P&L — all without an accounting degree. Free forever, no credit card.',
      url: 'https://myspend.info/',
    });
  }, []);

  return (
    <div style={{ fontFamily: T.fontBody, background: T.appBg, minHeight: '100vh' }}>

      {/* User indicator */}
      {user && (
        <div style={{
          background: T.amberBright, color: T.navy,
          padding: '8px 24px', textAlign: 'center',
          fontSize: 13, fontWeight: 700,
          boxShadow: '0 2px 8px rgba(217,123,40,0.3)',
        }}>
          📍 Logged in as {user?.email || 'Unknown'}
        </div>
      )}

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={{
        background: T.navy, padding: '0 24px', height: 60,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 2px 16px rgba(0,0,0,0.25)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LogoMark size={28} />
          <Wordmark size={18} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={onPricing} style={{
            background: 'none', border: 'none',
            color: 'rgba(255,255,255,0.6)', fontSize: 13, fontWeight: 600,
            padding: '8px 12px', cursor: 'pointer',
          }}>
            Pricing
          </button>
          {!user && <button onClick={onSignIn} style={{
            background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 10,
            color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
          }}>
            Sign In
          </button>}
          <button onClick={onSignUp} style={{
            background: T.gradTeal, border: 'none', borderRadius: 10,
            color: '#fff', fontSize: 13, fontWeight: 700,
            padding: '8px 16px', cursor: 'pointer',
            boxShadow: '0 3px 12px rgba(5,196,176,0.3)',
          }}>
            Get Started
          </button>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────────── */}
      <section style={{
        background: T.gradNavy, padding: '72px 24px 80px',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            background: 'rgba(5,196,176,0.15)', border: '1px solid rgba(5,196,176,0.3)',
            borderRadius: 20, padding: '5px 14px', marginBottom: 24,
          }}>
            <Zap size={13} color={T.tealBright} />
            <span style={{ fontSize: 12, fontWeight: 700, color: T.tealBright, letterSpacing: 0.5 }}>
              BOOKKEEPING FOR SOLOPRENEURS & SMALL BUSINESS
            </span>
          </div>
          <h1 style={{
            fontSize: 'clamp(32px, 6vw, 56px)', fontWeight: 900,
            color: '#fff', margin: '0 0 20px', lineHeight: 1.15, letterSpacing: -1,
          }}>
            AI powered finances<br />
            <span style={{ color: T.tealBright }}>for everyone</span>
          </h1>

          <p style={{
            fontSize: 18, color: 'rgba(255,255,255,0.65)', margin: '0 0 16px',
            lineHeight: 1.65, maxWidth: 540, marginLeft: 'auto', marginRight: 'auto',
          }}>
            Import your bank and credit card statements, scan receipts, mix business and personal — ask questions in plain English and stay tax-ready without learning accounting software.
          </p>

          <p style={{
            fontSize: 15, fontWeight: 800, color: T.tealBright, margin: '0 0 36px',
            letterSpacing: 0.3,
          }}>
            Absolutely free to use. No credit card needed to get started.
          </p>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onSignUp} style={{
              padding: '15px 32px', borderRadius: 14, border: 'none',
              background: T.gradTeal, color: '#fff', fontSize: 16, fontWeight: 800,
              cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              Start for Free <ArrowRight size={18} />
            </button>
            {!user && <button onClick={onSignIn} style={{
              padding: '15px 32px', borderRadius: 14,
              border: '1.5px solid rgba(255,255,255,0.2)',
              background: 'rgba(255,255,255,0.07)', color: 'rgba(255,255,255,0.85)',
              fontSize: 16, fontWeight: 700, cursor: 'pointer',
            }}>
              Sign In
            </button>}
          </div>

        </div>
      </section>

      {/* ── Trust bar ───────────────────────────────────────────────────────── */}
      <div style={{
        background: T.navyMid, padding: '16px 24px',
        display: 'flex', justifyContent: 'center', gap: 32, flexWrap: 'wrap',
      }}>
        {['Free forever, no credit card', 'Mix personal & business accounts', 'Ask your finances anything - AI powered', 'AES-256 encrypted locally'].map(t => (
          <div key={t} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Check size={13} color={T.tealBright} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.65)' }}>{t}</span>
          </div>
        ))}
      </div>

      {/* ── Bookkeeping callout ──────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: '#fff' }}>
        <div style={{
          maxWidth: 860, margin: '0 auto',
          display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 300px' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.tealPale, borderRadius: 12, padding: '8px 14px', marginBottom: 20,
            }}>
              <CreditCard size={18} color={T.tealDeep} />
              <span style={{ fontSize: 12, fontWeight: 800, color: T.tealDeep, letterSpacing: 0.5 }}>
                YOUR ACCOUNTS, YOUR WAY
              </span>
            </div>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px', lineHeight: 1.2 }}>
              Mix business and personal?<br />We handle that.
            </h2>
            <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 20px' }}>
              Most solopreneurs and small businesses run everything through the same bank account or credit card. MySpend.info lets you import those statements and classify each transaction as business or personal — without ever restructuring your accounts.
            </p>
            <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 20px' }}>
              Already scanned receipts on the go? AI automatically suggests which receipts and expenses match your imported CC or bank transactions — link them in one tap to eliminate double entries and keep your books clean.
            </p>
            <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 24px' }}>
              A real-time P&L, income vs. expense breakdown by category, and an IRS Schedule C summary — automatically organized, ready for your accountant.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Import credit card & bank statements',
                'Tag each transaction: business or personal',
                'AI links scanned receipts to CC transactions automatically',
                'Real-time P&L and financial summary view',
                'IRS Schedule C tax category breakdown',
              ].map(f => (
                <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <Check size={11} color={T.tealDeep} />
                  </div>
                  <span style={{ fontSize: 14, color: '#374151' }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ flex: '1 1 240px', display: 'flex', justifyContent: 'center' }}>
            <FSVMockup />
          </div>
        </div>
      </section>

      {/* ── Privacy & Security section ──────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: '#F0FDF4' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 40 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: '#DCFCE7', borderRadius: 12, padding: '8px 14px', marginBottom: 20,
            }}>
              <Shield size={18} color="#15803d" />
              <span style={{ fontSize: 12, fontWeight: 800, color: '#15803d', letterSpacing: 0.5 }}>YOUR DATA, YOUR RULES</span>
            </div>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 14px', lineHeight: 1.2 }}>
              Bank statements never have to<br />leave your device
            </h2>
            <p style={{ fontSize: 16, color: '#4B5563', maxWidth: 580, margin: '0 auto', lineHeight: 1.65 }}>
              When you import a bank or credit card statement, you choose where it lives. Keep it local and it stays encrypted in your browser — we never see it.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 24 }}>
            {[
              {
                Icon: HardDrive, color: '#15803d', bg: '#fff', border: '#BBF7D0',
                title: 'Local-only option',
                desc: 'Choose "Keep local" when importing a statement. Transaction data is stored only in your browser — never uploaded to any server.',
              },
              {
                Icon: Lock, color: '#0369a1', bg: '#fff', border: '#BFDBFE',
                title: 'AES-256 encrypted',
                desc: 'Local data is encrypted with a key derived from your account and device. Even raw access to your browser storage yields unreadable ciphertext.',
              },
              {
                Icon: Shield, color: '#7C3AED', bg: '#fff', border: '#E9D5FF',
                title: 'We can\'t read it',
                desc: 'The encryption key never leaves your device. There is no server-side copy, no master key, and no way for us — or anyone else — to decrypt your data.',
              },
            ].map(({ Icon, color, bg, border, title, desc }) => (
              <div key={title} style={{ background: bg, borderRadius: 16, padding: '24px', border: `1.5px solid ${border}` }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: border + '44',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
                }}>
                  <Icon size={22} color={color} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: T.navy, margin: '0 0 8px' }}>{title}</h3>
                <p style={{ fontSize: 13, color: '#4B5563', lineHeight: 1.6, margin: 0 }}>{desc}</p>
              </div>
            ))}
          </div>

          <div style={{
            background: '#fff', borderRadius: 12, border: '1px solid #BBF7D0',
            padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <Check size={16} color="#15803d" strokeWidth={3} style={{ flexShrink: 0 }} />
            <span style={{ fontSize: 14, color: '#374151', lineHeight: 1.5 }}>
              Prefer cloud sync? That works too — store statements in your secure account and access them from any device. The choice is yours, per file, at import time.
            </span>
          </div>
        </div>
      </section>

      {/* ── AI Bookkeeper callout ───────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: T.tealPale }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            background: '#fff', borderRadius: 12, padding: '8px 14px', marginBottom: 20,
          }}>
            <Sparkles size={18} color={T.tealDeep} />
            <span style={{ fontSize: 12, fontWeight: 800, color: T.tealDeep, letterSpacing: 0.5 }}>
              AI BOOKKEEPER
            </span>
          </div>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px', lineHeight: 1.2 }}>
            Ask your finances anything.
          </h2>
          <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 8px', maxWidth: 620 }}>
            MySpend AI is your personal financial analyst. Ask in plain English — get instant answers about your income, expenses, and P&L across all your reports and imported transactions.
          </p>
          <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 28px', maxWidth: 620 }}>
            Need to know your top categories? Want a chart of monthly spend over time? Just ask.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {[
              'Get a full P&L summary or drill into any category',
              'Ask about any date range, account, or transaction set',
              'Generate bar, line, and pie charts on demand',
              'Compare months, quarters, or custom periods side by side',
              'Works across all your reports and imported transactions',
              'Answers in seconds — no formulas or exports needed',
            ].map(f => (
              <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <Check size={11} color={T.tealDeep} />
                </div>
                <span style={{ fontSize: 14, color: '#374151' }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Receipt scanning callout ─────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: T.appBg }}>
        <div style={{
          maxWidth: 860, margin: '0 auto',
          display: 'flex', gap: 48, alignItems: 'center', flexWrap: 'wrap',
        }}>
          <div style={{ flex: '1 1 240px', display: 'flex', justifyContent: 'center', order: 2 }}>
            <ScanMockup />
          </div>
          <div style={{ flex: '1 1 300px', order: 1 }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              background: T.tealPale, borderRadius: 12, padding: '8px 14px', marginBottom: 20,
            }}>
              <Camera size={18} color={T.tealDeep} />
              <span style={{ fontSize: 12, fontWeight: 800, color: T.tealDeep, letterSpacing: 0.5 }}>
                AI RECEIPT CAPTURE
              </span>
            </div>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 16px', lineHeight: 1.2 }}>
              Zero typing.<br />Just point and scan.
            </h2>
            <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 20px' }}>
              Can't import a statement? No problem. Snap a photo of any receipt and our AI extracts every detail — vendor, subtotal, tax, tip, total, date, currency, and category. In seconds.
            </p>
            <p style={{ fontSize: 15, color: '#4B5563', lineHeight: 1.7, margin: '0 0 24px' }}>
              Works with restaurant bills, hotel folios, rideshare receipts, invoices, and more. Even PDFs.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {['Extracts vendor, amount, tax & tip automatically', 'Detects currency from the receipt', 'Flags mismatches so you can verify', 'Stores the receipt image with the expense'].map(f => (
                <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', background: T.tealPale, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    <Check size={11} color={T.tealDeep} />
                  </div>
                  <span style={{ fontSize: 14, color: '#374151' }}>{f}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ────────────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: '#fff' }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 12px' }}>
              Everything you need, nothing you don't
            </h2>
            <p style={{ fontSize: 16, color: '#6B7280', margin: 0 }}>
              No accounting degree. No expensive software. Just organized finances that are ready when tax time comes.
            </p>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20 }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{
                flex: '1 1 240px', background: T.appBg, borderRadius: 16,
                padding: '24px 20px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              }}>
                <div style={{
                  width: 42, height: 42, borderRadius: 12, background: f.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14,
                }}>
                  <f.icon size={20} color={f.color} />
                </div>
                <h3 style={{ fontSize: 15, fontWeight: 800, color: T.navy, margin: '0 0 8px' }}>{f.title}</h3>
                <p style={{ fontSize: 13, color: '#6B7280', lineHeight: 1.6, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Persona guides ──────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: T.appBg }}>
        <div style={{ maxWidth: 860, margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 12px' }}>
              Built for how you actually work
            </h2>
            <p style={{ fontSize: 16, color: '#6B7280', margin: 0 }}>
              Whether you're running a business, billing clients, driving for a platform, or tracking a household — MySpend.info fits your workflow.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 }}>
            {PERSONAS.map(p => {
              const Icon = p.icon;
              return (
                <div key={p.badge} style={{
                  background: '#fff', borderRadius: 16, padding: '24px',
                  border: '1.5px solid #E5E7EB', display: 'flex', flexDirection: 'column', gap: 0,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
                    <div style={{
                      width: 48, height: 48, borderRadius: 14, background: p.bg,
                      display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                    }}>
                      <Icon size={22} color={p.color} />
                    </div>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 800, color: p.color, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 2 }}>
                        {p.badge}
                      </div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: T.navy, lineHeight: 1.2 }}>{p.title}</div>
                    </div>
                  </div>
                  <p style={{ fontSize: 14, color: '#4B5563', lineHeight: 1.65, margin: '0 0 16px' }}>{p.desc}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 20 }}>
                    {p.bullets.map(b => (
                      <div key={b} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Check size={13} color={p.color} strokeWidth={3} />
                        <span style={{ fontSize: 13, color: '#374151' }}>{b}</span>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      if (p.page) window.location.href = `/?page=${p.page}`;
                      else onSignUp();
                    }}
                    style={{
                      marginTop: 'auto', alignSelf: 'flex-start',
                      padding: '8px 16px', borderRadius: 10,
                      border: `1.5px solid ${p.color}`, background: 'transparent',
                      color: p.color, fontSize: 13, fontWeight: 700,
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = p.bg; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    {p.primaryCta || 'Find out more'} <ArrowRight size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Pricing teaser ──────────────────────────────────────────────────── */}
      <section style={{ padding: '72px 24px', background: '#fff', textAlign: 'center' }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: T.navy, margin: '0 0 12px' }}>
            Simple, transparent pricing
          </h2>
          <p style={{ fontSize: 16, color: '#6B7280', margin: '0 0 8px', lineHeight: 1.6 }}>
            MySpend.info is <strong style={{ color: T.tealDeep }}>free forever</strong> — no expiry, no tricks. Paid plans unlock higher usage limits and advanced export features for power users.
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 28, flexWrap: 'wrap' }}>
            <button onClick={onPricing} style={{
              padding: '13px 28px', borderRadius: 12, border: `2px solid ${T.tealDeep}`,
              background: 'transparent', color: T.tealDeep, fontSize: 14, fontWeight: 800,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
              See Plans &amp; Pricing <ArrowRight size={16} />
            </button>
            <button onClick={onSignUp} style={{
              padding: '13px 28px', borderRadius: 12, border: 'none',
              background: T.gradTeal, color: '#fff', fontSize: 14, fontWeight: 800,
              cursor: 'pointer', boxShadow: '0 4px 15px rgba(5,196,176,0.3)',
            }}>
              Start Free
            </button>
          </div>
        </div>
      </section>

      {/* ── Final CTA ───────────────────────────────────────────────────────── */}
      <section style={{
        padding: '72px 24px', background: T.gradNavy, textAlign: 'center',
      }}>
        <div style={{ maxWidth: 540, margin: '0 auto' }}>
          <div style={{ marginBottom: 20 }}>
            <LogoMark size={48} />
          </div>
          <h2 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 900, color: '#fff', margin: '0 0 16px' }}>
            Stop fighting your finances.<br />Start working with them.
          </h2>
          <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.6)', margin: '0 0 32px', lineHeight: 1.6 }}>
            Join thousands of solopreneurs, consultants, and small businesses who track smarter with MySpend.info. Free to start, always.
          </p>
          <button onClick={onSignUp} style={{
            padding: '16px 40px', borderRadius: 14, border: 'none',
            background: T.gradTeal, color: '#fff', fontSize: 16, fontWeight: 800,
            cursor: 'pointer', boxShadow: '0 6px 24px rgba(5,196,176,0.35)',
            display: 'inline-flex', alignItems: 'center', gap: 8,
          }}>
            Create Free Account <ArrowRight size={18} />
          </button>
          <div style={{ marginTop: 16 }}>
            <button onClick={onSignIn} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)',
              fontSize: 14, cursor: 'pointer', padding: '6px 12px',
            }}>
              Already have an account? Sign in →
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <footer style={{ background: T.navy, padding: '48px 32px 32px' }}>
        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 36 }}>
          <LogoMark size={22} />
          <Wordmark size={15} />
        </div>

        {/* Link columns */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '32px 48px', marginBottom: 36 }}>
          {/* Product */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>Product</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <button onClick={onPricing} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left', color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Pricing</button>
              <a href="/help/" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Help Center</a>
              <a href="/help/importing-transactions.html" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Import Guide</a>
            </div>
          </div>

          {/* Company */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>Company</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <a href="/about" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>About Us</a>
              <a href="/blog" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Blog</a>
              <a href="mailto:support@myspend.info" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Contact Us</a>
            </div>
          </div>

          {/* Legal */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>Legal</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <a href="/terms" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Terms of Service</a>
              <a href="/privacy" style={{ color: 'rgba(255,255,255,0.55)', fontSize: 13, lineHeight: 2, textDecoration: 'none' }}>Privacy Policy</a>
            </div>
          </div>
        </div>

        {/* Bottom rule + copyright */}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.08)', paddingTop: 20 }}>
          <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12 }}>
            © {new Date().getFullYear()} MySpend.info. All rights reserved.
          </span>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
